import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { addBreadcrumb } from '@sentry/react';
import {
  DeviceType_Enum,
  LayoutType_Enum,
  LineKey,
} from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import isEqual from 'lodash-es/isEqual';
import { useQueryClient } from 'react-query';
import { DevicesApi, DevicesQueries, DevicesQueryKey } from '@frontend/api-devices';
import { LineKeysApi, LineKeysTypes } from '@frontend/api-line-keys';
import { useTranslation } from '@frontend/i18n';
import { Page } from '@frontend/page';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery, useMutation } from '@frontend/react-query-helpers';
import { useNavSize } from '@frontend/responsiveness';
import { useNavigationBlocker, useSettingsNavigate } from '@frontend/settings-routing';
import { SettingsPageLayout } from '@frontend/settings-ui';
import { genUUIDV4 } from '@frontend/string';
import { theme } from '@frontend/theme';
import { ContentLoader, useAlert, useModalControl } from '@frontend/design-system';
import { LineKeyCards } from '../../../components/devices/line-keys/cards-layout/cards';
import { LineKeyFooter } from '../../../components/devices/line-keys/footer';
import { Layout } from '../../../components/devices/line-keys/preview-layout/layout';
import {
  LineKeyContext,
  LineKeyProvider,
  useLineKeyState,
} from '../../../components/devices/line-keys/store/line-key-provider';
import { isLocalContactExtension } from '../../../components/devices/line-keys/utils';
import { queryKeys } from '../../../query-keys';
import { CancelEditModal } from '../../../utils/cancel-edit.modal';
import { logError } from '../../../utils/loggers';

export const LineKeyWrapper = ({ deviceId }: { deviceId: string }) => {
  const { globalAvailableLocationIds: locationIds, settingsTenantLocation } = usePhoneSettingsShallowStore(
    'globalAvailableLocationIds',
    'settingsTenantLocation'
  );
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';

  const { data: lineKeyOptions } = useQuery({
    queryKey: [tenantId, 'line-keys', 'mapped-line-key-options'],
    queryFn: () =>
      LineKeysApi.GetLineKeyOptions({
        tenantId,
      }),
    select: ({ localContacts, officeExtensions, userExtensions, voicemailExtensions }) => {
      const allExtensions: LineKeysTypes.ExtensionsUnionType[] = [
        ...userExtensions,
        ...voicemailExtensions,
        ...localContacts,
        ...officeExtensions,
      ];
      const mappedExtensions = allExtensions.reduce<Record<string, LineKeysTypes.ExtensionsUnionType>>((acc, sum) => {
        const id = isLocalContactExtension(sum) ? sum.contactId : sum.extensionId;
        return {
          ...acc,
          [id]: sum,
        };
      }, {});

      return mappedExtensions;
    },
    meta: {
      error: {
        message: 'LineKeyWrapper:GetLineKeyOptions | Failed to fetch Line Key options',
        data: { tenantId },
      },
    },
  });

  const { data: lineKeys, isLoading: isLoadingLineKeys } = useQuery({
    queryKey: [tenantId, deviceId, ...queryKeys.settings.listLineKeys()],
    queryFn: () => LineKeysApi.GetLineKeys(deviceId),
    select: (lineKeysResponse) => {
      const obj: Record<string, LineKeysTypes.GetLineKeysType['output']['lineKeys'][number]> = {};
      lineKeysResponse?.lineKeys.forEach((item) => {
        const id = genUUIDV4();
        obj[id] = item;
      });
      return obj;
    },
    meta: {
      error: {
        message: 'LineKeyWrapper:GetLineKeys | Failed to fetch Line Keys',
        data: { tenantId, deviceId },
      },
    },
  });

  const { data: device, isLoading: isLoadingDevice } = DevicesQueries.useGetDevicesList(
    {
      locationIds,
      deviceType: DeviceType_Enum.DESK_PHONE,
      tenantId: settingsTenantLocation?.phoneTenantId,
    },
    {
      select: ({ devices }) => {
        return devices.find((listDevice) => listDevice.deviceId === deviceId);
      },
      meta: {
        error: {
          message: 'LineKeyWrapper:GetDevicesList | Failed to fetch Device details',
          data: { tenantId, deviceId },
        },
      },
    }
  );

  const { data: model, isLoading: isLoadingModel } = useQuery({
    queryKey: [tenantId, 'line-keys', 'device-models'],
    queryFn: () => LineKeysApi.GetDeviceModelInfo(),
    select: ({ models }) => {
      return models.find((deviceModel) => device?.model === deviceModel.model);
    },
    meta: {
      error: {
        message: 'LineKeyWrapper:GetDeviceModelInfo | Failed to fetch Device model info',
        data: { tenantId, deviceId },
      },
    },
  });

  const isLoading = isLoadingModel || isLoadingDevice || isLoadingLineKeys;
  if (isLoading) return <ContentLoader show />;

  const keys = model?.layoutType === LayoutType_Enum.TOUCH_SCREEN ? model.touchKeys?.maxKeys : model?.sideKeys?.maxKeys;
  const maxKeys = (keys && keys - (device?.sipProfiles?.length ?? 1)) ?? Infinity;

  return (
    <LineKeyContext.Provider
      value={{
        lineKeyOptions,
        lineKeys,
        model,
        maxKeys,
        device,
      }}
    >
      <LineKeyProvider>
        <SettingsPageLayout css={{ scrollbarGutter: 'stable', position: 'relative' }}>
          <Page
            bgColor={theme.colors.neutral5}
            css={css`
              padding: 0;
              display: flex;
              flex-direction: column;
              flex-grow: 1;
              height: 100%;

              header > nav {
                margin: 0;
              }

              .page-header {
                margin: 0;
                padding: ${theme.spacing(3, 0, 1, 3)};
              }
            `}
          >
            <LineKeyContainer model={model} lineKeys={lineKeys} device={device} />
          </Page>
        </SettingsPageLayout>
      </LineKeyProvider>
    </LineKeyContext.Provider>
  );
};

type Props = {
  device: LineKeysTypes.ListDeviceType | undefined;
  model: LineKeysTypes.ModelInfoType | undefined;
  lineKeys: Record<string, LineKeysTypes.GetLineKeysType['output']['lineKeys'][number]> | undefined;
};

const LineKeyContainer = ({ model, lineKeys, device }: Props) => {
  const { t } = useTranslation('phone');
  const { finalLineKeys } = useLineKeyState(['finalLineKeys']);
  const deviceId = device?.deviceId ?? '';
  const [showDeviceLayoutPreview, setShowDeviceLayoutPreview] = useState(false);
  const [showFirstPageOnHomeScreen, setShowFirstPageOnHomeScreen] = useState<boolean>();
  const navSize = useNavSize();
  const isMobile = navSize.isLte('medium');
  const alerts = useAlert();
  const queryClient = useQueryClient();
  const { navigate } = useSettingsNavigate();

  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId ?? '';

  const { data: deviceConfigResponse } = useQuery({
    queryKey: [deviceId, 'devices-primitive-type-config'],
    queryFn: () =>
      DevicesApi.GetPrimitiveTypeConfig({
        primitiveType: 'device',
        primitiveId: deviceId,
      }),
    enabled: !!deviceId,
    meta: {
      error: {
        message: 'LineKeyContainer:GetPrimitiveTypeConfig | Failed to fetch device config',
        data: { tenantId, deviceId },
      },
    },
  });
  const deviceConfig = useMemo(() => deviceConfigResponse?.config ?? {}, [deviceConfigResponse?.config]);
  const initialShowFirstPageOnHomeScreen =
    typeof deviceConfig.showFirstPageKeysOnHomeScreen === 'boolean'
      ? deviceConfig.showFirstPageKeysOnHomeScreen
      : false;
  const effectiveShowFirstPageOnHomeScreen = showFirstPageOnHomeScreen ?? initialShowFirstPageOnHomeScreen;

  const { modalProps: cancelModalProps, triggerProps: cancelTriggerProps } = useModalControl();

  const initialLineKeyValues = useMemo(() => {
    return Object.values(lineKeys ?? {});
  }, [lineKeys]);

  const lineKeyValues = useMemo(() => {
    return Object.values(finalLineKeys ?? lineKeys ?? {});
  }, [lineKeys, finalLineKeys]);

  const hasUnsavedChanges = useMemo(() => {
    const hasLineKeyChanges = !!finalLineKeys && !isEqual(Object.values(finalLineKeys), initialLineKeyValues);
    const hasHomeScreenToggleChanges = effectiveShowFirstPageOnHomeScreen !== initialShowFirstPageOnHomeScreen;

    return hasLineKeyChanges || hasHomeScreenToggleChanges;
  }, [effectiveShowFirstPageOnHomeScreen, finalLineKeys, initialLineKeyValues, initialShowFirstPageOnHomeScreen]);

  const { proceed } = useNavigationBlocker(
    {
      shouldBlockFn: () => {
        if (hasUnsavedChanges || cancelModalProps.show) {
          cancelTriggerProps.onClick();
          return true;
        }

        return false;
      },
      enableBeforeUnload: true,
    },
    [hasUnsavedChanges, cancelModalProps.show]
  );

  const { mutate: onSave, isLoading } = useMutation(
    async ({
      config,
      nextLineKeys,
      shouldSaveDeviceConfig,
      shouldSaveLineKeys,
    }: {
      config: Record<string, unknown>;
      nextLineKeys: LineKey[];
      shouldSaveDeviceConfig: boolean;
      shouldSaveLineKeys: boolean;
    }) => {
      const lineKeysResponse = shouldSaveLineKeys
        ? await LineKeysApi.ReplaceLineKeys({ deviceId, lineKeys: nextLineKeys })
        : undefined;

      if (shouldSaveDeviceConfig) {
        await DevicesApi.UploadMedia({
          primitiveType: 'device',
          primitiveId: deviceId,
          config,
        });
      }

      return { lineKeysResponse };
    },
    {
      onSuccess: ({ lineKeysResponse }, request) => {
        if (lineKeysResponse) {
          queryClient.setQueryData([tenantId, deviceId, ...queryKeys.settings.listLineKeys()], () => ({
            lineKeys: lineKeysResponse.lineKeys,
          }));
          queryClient.invalidateQueries([tenantId, ...DevicesQueryKey.queryKeys.devicesList()]);
        }

        if (request.shouldSaveDeviceConfig) {
          queryClient.invalidateQueries([deviceId, 'devices-primitive-type-config']);
        }

        alerts.success(t('Successfully saved Line Keys layout'));
      },
      onError: (error) => {
        alerts.error(t('Line Keys did not Save. Please contact Weave Support'));
        logError('LineKeyContainer:ReplaceLineKeysMutation | Failed to save Line Keys', {
          data: {
            tenantId,
            deviceId,
            isMobile,
            hasUnsavedChanges,
            error,
          },
        });
      },
    }
  );

  const handleSave = useCallback(
    (shouldProceed?: boolean) => {
      const hasLineKeyChanges = !!finalLineKeys && !isEqual(Object.values(finalLineKeys), initialLineKeyValues);
      const hasHomeScreenToggleChanges = effectiveShowFirstPageOnHomeScreen !== initialShowFirstPageOnHomeScreen;

      addBreadcrumb({
        category: 'callback',
        message: 'Saving Line Keys in handleSave()',
        data: {
          deviceId,
          shouldProceed,
          isMobile,
          hasUnsavedChanges,
        },
      });
      onSave(
        {
          nextLineKeys: Object.values(finalLineKeys ?? lineKeys ?? {}),
          shouldSaveLineKeys: hasLineKeyChanges,
          shouldSaveDeviceConfig: hasHomeScreenToggleChanges,
          config: {
            ...deviceConfig,
            showFirstPageKeysOnHomeScreen: effectiveShowFirstPageOnHomeScreen,
          },
        },
        {
          onSuccess: () => {
            if (shouldProceed) {
              proceed();
            }
          },
        }
      );
    },
    [
      deviceConfig,
      deviceId,
      effectiveShowFirstPageOnHomeScreen,
      finalLineKeys,
      hasUnsavedChanges,
      initialLineKeyValues,
      initialShowFirstPageOnHomeScreen,
      isMobile,
      lineKeys,
      onSave,
      proceed,
    ]
  );

  return (
    <div
      id='container'
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Page.Header>
        <Page.Header.SettingsBreadcrumbs
          breadcrumbs={[
            { label: t('Phone'), to: '/phone/main' },
            { label: t('Line Keys'), to: '/phone/line-keys' },
            { label: device?.name ?? t('Unknown Name') },
          ]}
        />
      </Page.Header>
      <Page.Body
        css={css`
          display: flex;
          flex-grow: 1;
          overflow: hidden;
        `}
      >
        <div style={{ flexGrow: 1, display: 'flex' }}>
          {!showDeviceLayoutPreview && (
            // Left Section (Scrollable)
            <LineKeyCards setShowDeviceLayoutPreview={setShowDeviceLayoutPreview} deviceId={deviceId} />
          )}
          {(!isMobile || showDeviceLayoutPreview) && (
            // Right Section (Device)
            <Layout
              model={model}
              device={device}
              lineKeys={lineKeyValues}
              isPreviewEnabled={isMobile}
              setShowDeviceLayoutPreview={setShowDeviceLayoutPreview}
              showFirstPageOnHomeScreen={effectiveShowFirstPageOnHomeScreen}
              onShowFirstPageOnHomeScreenChange={setShowFirstPageOnHomeScreen}
            />
          )}
        </div>
      </Page.Body>
      <LineKeyFooter
        isLoading={isLoading}
        onSaveClick={handleSave}
        onCancelClick={() => {
          navigate({ to: `/phone/line-keys` });
        }}
      />

      <CancelEditModal
        disabled={isLoading}
        modalProps={cancelModalProps}
        onQuitClick={proceed}
        onSaveAndQuitClick={() => handleSave(true)}
      />
    </div>
  );
};
